import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiResponse } from '../models/product.dto';
import { AuthService } from './auth.service';

export interface ArkifiHubProductRatingDto {
  id: string;
  productId: string;
  arkifiHubCustomerId: string;
  rating: number;
  review?: string;
  reviewTitle?: string;
  isVerifiedPurchase: boolean;
  isApproved: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  qualityRating?: number;
  valueRating?: number;
  shippingRating?: number;
  customerServiceRating?: number;
  customerDisplayName: string;
  customerInitials: string;
  platformId: string;
  businessId: string;
  environmentType: string;
}

export interface CreateArkifiHubProductRatingDto {
  productId: string;
  platformId: string;
  environmentType: number; // 1 = Test, 2 = Live (matches EnviromentType enum)
  rating: number;
  review?: string;
  reviewTitle?: string;
  qualityRating?: number;
  valueRating?: number;
  shippingRating?: number;
  customerServiceRating?: number;
}

export interface UpdateArkifiHubProductRatingDto {
  id: string;
  rating: number;
  review?: string;
  reviewTitle?: string;
  qualityRating?: number;
  valueRating?: number;
  shippingRating?: number;
  customerServiceRating?: number;
}

export interface ArkifiHubProductRatingSummaryDto {
  productId: string;
  averageRating: number;
  totalRatings: number;
  totalReviews: number;
  oneStarCount: number;
  twoStarCount: number;
  threeStarCount: number;
  fourStarCount: number;
  fiveStarCount: number;
  oneStarPercentage: number;
  twoStarPercentage: number;
  threeStarPercentage: number;
  fourStarPercentage: number;
  fiveStarPercentage: number;
}

@Injectable({
  providedIn: 'root'
})
export class ProductRatingService {
  private apiUrl = `${environment.apiUrl}/api/arkifihubproductrating`;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) { }

  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    if (!token) {
      console.warn('No authentication token found. User may need to log in again.');
    }
    if (token) {
      return new HttpHeaders({
        'Authorization': `Bearer ${token}`
      });
    }
    return new HttpHeaders();
  }

  getProductRatings(
    productId: string,
    platformId: string,
    env: 'Live' | 'Test',
    page: number = 1,
    pageSize: number = 10
  ): Observable<ApiResponse<ArkifiHubProductRatingDto[]>> {
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', env)
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<ApiResponse<ArkifiHubProductRatingDto[]>>(`${this.apiUrl}/product/${productId}`, { params });
  }

  getProductRatingSummary(
    productId: string,
    platformId: string,
    env: 'Live' | 'Test'
  ): Observable<ApiResponse<ArkifiHubProductRatingSummaryDto>> {
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', env);

    return this.http.get<ApiResponse<ArkifiHubProductRatingSummaryDto>>(`${this.apiUrl}/product/${productId}/summary`, { params });
  }

  createRating(rating: CreateArkifiHubProductRatingDto): Observable<ApiResponse<ArkifiHubProductRatingDto>> {
    return this.http.post<ApiResponse<ArkifiHubProductRatingDto>>(
      `${this.apiUrl}/create`, 
      rating,
      { headers: this.getAuthHeaders() }
    );
  }

  updateRating(rating: UpdateArkifiHubProductRatingDto): Observable<ApiResponse<ArkifiHubProductRatingDto>> {
    return this.http.put<ApiResponse<ArkifiHubProductRatingDto>>(
      `${this.apiUrl}/update`, 
      rating,
      { headers: this.getAuthHeaders() }
    );
  }

  canRateProduct(
    productId: string,
    platformId: string,
    env: 'Live' | 'Test'
  ): Observable<ApiResponse<boolean>> {
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', env);

    return this.http.get<ApiResponse<boolean>>(
      `${this.apiUrl}/product/${productId}/can-rate`, 
      { params, headers: this.getAuthHeaders() }
    );
  }
}
